import { useCallback, useEffect, useRef, useState } from "react";
import type { MapRef } from "react-map-gl";

/**
 * Notices when a map's WebGL context dies and rebuilds the map, instead of leaving the user
 * looking at a grey square.
 *
 * WHY
 * ---
 * Chrome keeps a browser-wide cap on live WebGL contexts (around 16). Past it, it reclaims the
 * oldest ones and refuses to hand out new ones, and mapbox-gl then reports "Map is not supported
 * by this browser" -- which reads like the browser lacks WebGL, when really the tab has simply
 * run out of contexts. The same thing happens for reasons we do not control: GPU driver resets,
 * the OS reclaiming resources, memory pressure, remote desktop sessions.
 *
 * None of that is preventable from here, so the fix is to notice and rebuild. This is the pattern
 * VehicleHistory has carried on its own since the grey-map incident; every other map had no
 * recovery at all and stayed grey until the user thought to reload. Lifted here so they share it.
 *
 * HOW
 * ---
 * Put the returned `mapKey` on the <Map> as its `key`. Remounting is what disposes the dead map
 * and builds a fresh one -- react-map-gl calls map.remove() on unmount, so the dead context is
 * released rather than leaked.
 *
 *     const { mapKey, mapUnavailable, retry } = useMapWebglRecovery(mapRef);
 *     <Map key={mapKey} ref={mapRef} ... />
 *
 * Two triggers, because one is not enough:
 *   - the canvas `webglcontextlost` event, for a context that dies while we are using it;
 *   - a watchdog, for a map that is handed a context that is ALREADY dead. There is no event in
 *     that case and the map just never finishes loading, which is the state the grey screen was
 *     actually found in.
 *
 * Rebuilds are capped. If the context will not survive, retrying forever flickers at the user and
 * burns GPU; after the cap `mapUnavailable` goes true and the caller shows its own message. `retry`
 * resets the cap, for a "Try again" the user asks for.
 */

/** Rebuild attempts before we stop and tell the user. */
const MAX_REBUILDS = 3;

/** How long a map gets to finish loading before we suspect its context arrived dead. */
const LOAD_WATCHDOG_MS = 8000;

/** Shown when rebuilding has not helped. Deliberately browser-agnostic: the usual cause is too many open tabs, not a browser that cannot do WebGL. */
export const mapUnavailableMessage =
  "The map could not start. This usually means too many browser tabs are using graphics at once — close some tabs and reload.";

export interface MapWebglRecovery {
  /** Put this on the <Map> as its `key`. It changes to force a rebuild. */
  mapKey: number;
  /**
   * Put this on the <Map> as `reuseMaps`, instead of a bare `reuseMaps`.
   *
   * react-map-gl's reuse pool keeps the underlying Mapbox map alive across unmounts and hands the
   * same instance -- and the same canvas -- to the next mount. That is what we want normally: it
   * stops a remount taking a second WebGL context. But it is exactly wrong after a context loss,
   * because the map we get back is the dead one, so the rebuild changes nothing. Verified: forcing
   * WEBGL_lose_context with a plain `reuseMaps` left the same canvas element with a dead context.
   *
   * So reuse is on until a context is lost, and off for the rebuild that follows.
   */
  reuseMaps: boolean;
  /** True once rebuilding has been tried MAX_REBUILDS times and still failed. */
  mapUnavailable: boolean;
  /** Clears the failure and starts the rebuild budget again. */
  retry: () => void;
}

export const useMapWebglRecovery = (
  mapRef: React.RefObject<MapRef | null>
): MapWebglRecovery => {
  const [mapKey, setMapKey] = useState(0);
  const [mapUnavailable, setMapUnavailable] = useState(false);
  const rebuildsRef = useRef(0);

  const retry = useCallback(() => {
    rebuildsRef.current = 0;
    setMapUnavailable(false);
    setMapKey((k) => k + 1);
  }, []);

  useEffect(() => {
    if (mapUnavailable) return undefined;

    let cancelled = false;
    let canvas: HTMLCanvasElement | undefined;
    let watchdog: number | undefined;
    let attach: number | undefined;

    const rebuild = () => {
      if (cancelled) return;
      if (rebuildsRef.current >= MAX_REBUILDS) {
        setMapUnavailable(true);
        return;
      }
      rebuildsRef.current += 1;
      setMapKey((k) => k + 1);
    };

    const handleContextLost = (ev: Event) => {
      // Asks the browser to allow the context to be restored at all; without it the canvas is
      // permanently dead and even a rebuilt map on the same canvas would fail.
      ev.preventDefault();
      rebuild();
    };

    // react-map-gl creates the underlying map during its own mount, so the ref is usually set by
    // the time this runs -- but not on the very first paint of a lazily rendered map. Poll briefly
    // rather than miss the attach entirely.
    const tryAttach = () => {
      if (cancelled) return;
      const map = mapRef.current?.getMap();
      if (!map) {
        attach = window.setTimeout(tryAttach, 100);
        return;
      }

      canvas = map.getCanvas();
      canvas?.addEventListener("webglcontextlost", handleContextLost);

      watchdog = window.setTimeout(() => {
        if (cancelled) return;
        const live = mapRef.current?.getMap();
        if (!live || live.loaded()) return;
        const lost = (live as unknown as {
          painter?: { context?: { gl?: { isContextLost?: () => boolean } } };
        }).painter?.context?.gl?.isContextLost?.();
        if (lost) rebuild();
      }, LOAD_WATCHDOG_MS);
    };

    tryAttach();

    return () => {
      cancelled = true;
      if (attach !== undefined) window.clearTimeout(attach);
      if (watchdog !== undefined) window.clearTimeout(watchdog);
      canvas?.removeEventListener("webglcontextlost", handleContextLost);
    };
  }, [mapKey, mapUnavailable, mapRef]);

  return { mapKey, reuseMaps: mapKey === 0, mapUnavailable, retry };
};
